import { defineStore } from "pinia"
import { fetchApiJson } from "@/services/repowerAPIService.js"
import {
  usePortfolioStoreFactory,
  useAllocationPointStoreFactory,
  useAssetStoreFactory,
} from "@/stores/repower/meteringPointStoreFactories.js"
import useFilterStore from "@/stores/filterStore.js"

const MAX_QUERY_STRING_ASSETS = 20

/**
 * Group an array of objects by each entry's 'portfolioId' property. The result
 * is an array with portfolioId as index and array values forming subsets of the
 * original array.
 */
function groupByPortfolio(array) {
  const grouped = []
  for (const item of array) {
    if (!grouped[item.portfolioId]) {
      grouped[item.portfolioId] = []
    }
    grouped[item.portfolioId].push(item)
  }
  return grouped
}

export default defineStore("meteringPointsStore", {
  state: () => ({
    overviewPromise: null,
    portfolios: [],
    allocationPoints: [],
    assets: [],
    loading: false,
  }),
  getters: {
    meteringPoints: (state) => [
      ...state.portfolios,
      ...state.allocationPoints,
      ...state.assets,
    ],
    allocationPointsByPortfolio: (state) =>
      groupByPortfolio(state.allocationPoints),
    assetsByPortfolio: (state) => groupByPortfolio(state.assets),
    /**
     * Generate a query string for each portfolio that includes the portfolio
     * name and the names of the first MAX_QUERY_STRING_ASSETS assets. This
     * allows users to search portfolios by the contained assets as well.
     */
    queryStringsByPortfolio(state) {
      const queryStrings = {}
      for (const portfolio of state.portfolios) {
        const assets =
          state.assetsByPortfolio[portfolio.id]?.slice(
            0,
            MAX_QUERY_STRING_ASSETS,
          ) ?? []
        queryStrings[portfolio.id] =
          `${portfolio.name} ${assets.map((asset) => asset.name).join(" ")}`
      }
      return queryStrings
    },
  },
  actions: {
    loadMeteringPointsOverview(onSuccess, onFail) {
      this.loading = true
      this.overviewPromise = fetchApiJson("/portfolios/overview")
        .then((data) => {
          // Create portfolio stores
          this.portfolios = []
          for (const portfolio of data.portfolios) {
            const portfolioStore = usePortfolioStoreFactory(portfolio.id)
            portfolioStore.setMeteringPoint(portfolio)
            this.portfolios.push(portfolioStore)
          }
          // Create allocation point stores
          this.allocationPoints = []
          for (const allocationPoint of data.allocation_points) {
            const allocationPointStore = useAllocationPointStoreFactory(
              allocationPoint.id,
            )
            allocationPointStore.setMeteringPoint(allocationPoint)
            this.allocationPoints.push(allocationPointStore)
            // Add energy hub allocation points to the corresponding portfolio
            if (allocationPoint.energy_hub_id) {
              const portfolioStore = this.findPortfolio(
                allocationPoint.portfolio_id,
              )
              if (portfolioStore === undefined) {
                throw new Error(
                  `Allocation point ${allocationPoint.id} has unknown portfolio ${allocationPoint.portfolio_id}`,
                )
              } else {
                portfolioStore.energyHubAPs.push(allocationPointStore)
              }
            }
          }
          // Create asset stores
          this.assets = []
          for (const asset of data.assets) {
            const assetStore = useAssetStoreFactory(asset.id)
            assetStore.setMeteringPoint(asset)
            this.assets.push(assetStore)
          }
          // Run success callback
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
          this.overviewPromise = null
        })
        .finally(() => {
          this.loading = false
        })
      return this.overviewPromise
    },
    loadMeteringPoints(onLoadFail) {
      this.loadMeteringPointsOverview(
        useFilterStore().setDefaultPortfolio,
        onLoadFail,
      )
    },
    findMeteringPoint(id) {
      return this.meteringPoints.find((mp) => mp.id === id)
    },
    findPortfolio(id) {
      return this.portfolios.find((p) => p.id === id)
    },
  },
})
